import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { 
  Gamepad2, Plus, Sparkles, FolderOpen, LogOut, Settings, Trash2,
  Loader2, Play, Edit, Box, Swords, Brain, Puzzle, Building, Shield,
  Image as ImageIcon, MessageSquare, Store, Crown, Home
} from 'lucide-react';
import { toast } from 'sonner';
import { usePushNotifications, notifyInTab } from '../lib/push';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const genreIcons = {
  platformer: Box,
  fps: Swords,
  rpg: Brain,
  puzzle: Puzzle,
  simulation: Building
};

const genreColors = {
  platformer: "genre-platformer",
  fps: "genre-fps",
  rpg: "genre-rpg",
  puzzle: "genre-puzzle",
  simulation: "genre-simulation"
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // New project form
  const [newName, setNewName] = useState('');
  const [newGenre, setNewGenre] = useState('platformer');
  const [newPrompt, setNewPrompt] = useState('');
  const [dmUnread, setDmUnread] = useState(0);
  const { enable: enablePush } = usePushNotifications();

  // Poll DM unread count so the badge feels alive.
  useEffect(() => {
    let cancelled = false;
    let prev = 0;
    const fetchUnread = async () => {
      try {
        const r = await axios.get(`${API}/dm/unread_count`, { withCredentials: true });
        if (cancelled) return;
        const n = r.data?.unread || 0;
        if (n > prev && prev >= 0) {
          notifyInTab('New PlayMold message', `You have ${n} unread message${n === 1 ? '' : 's'}.`);
        }
        prev = n;
        setDmUnread(n);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const t = setInterval(fetchUnread, 25000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(res.data);
    } catch (error) {
      console.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (useAI = false) => {
    if (!newName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    // Feature gating - check project limits
    const tierLimits = { free: 3, standard: 10, premium: Infinity };
    const userTier = user?.tier || 'free';
    const limit = tierLimits[userTier];
    
    if (projects.length >= limit) {
      toast.error(`${userTier === 'free' ? 'Free' : 'Standard'} plan limit reached (${limit} projects). Upgrade to create more!`);
      navigate('/pricing');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        name: newName,
        genre: newGenre,
        prompt: useAI ? newPrompt : ''
      };
      
      const res = await axios.post(`${API}/projects`, payload, { withCredentials: true });
      toast.success(useAI ? 'Game generated with AI!' : 'Project created!');
      setDialogOpen(false);
      setNewName('');
      setNewPrompt('');
      navigate(`/editor/${res.data.project_id}`);
    } catch (error) {
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const deleteProject = async (projectId) => {
    const tier = (user?.tier || 'free').toLowerCase();
    const role = user?.role;
    const isPremium = role === 'admin' || tier === 'pro' || tier === 'studio';
    const days = role === 'admin' ? 365 : tier === 'studio' ? 90 : tier === 'pro' ? 60 : 14;
    const message = isPremium
      ? `This will move the project to your Trash. Auto-deletion in ${days} days.\nGo to /trash if you want to delete it permanently.`
      : `Delete this project? It will be moved to Trash and auto-deleted after ${days} days.`;
    if (!window.confirm(message)) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="dashboard">
      {/* Header */}
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button
              variant="outline" size="sm"
              onClick={() => navigate('/')}
              className="bg-transparent border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 h-9 shrink-0"
              data-testid="dashboard-home-btn"
              title="Back to homepage"
            >
              <Home className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Home</span>
            </Button>
            <div className="w-10 h-10 rounded-sm bg-[#06B6D4]/20 border border-[#06B6D4]/50 flex items-center justify-center">
              <Gamepad2 className="w-5 h-5 text-[#06B6D4]" />
            </div>
            <span className="font-black text-xl tracking-tight text-white">PlayMold</span>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <span className="text-sm text-zinc-400 hidden md:inline">Welcome, {user?.name}</span>
            <Button variant="ghost" size="icon" onClick={() => navigate('/assets')} className="text-zinc-400 hover:text-[#06B6D4]" title="My Assets" data-testid="dashboard-assets-btn">
              <ImageIcon className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/chat')} className="text-zinc-400 hover:text-[#06B6D4]" title="AI Chat" data-testid="dashboard-chat-btn">
              <MessageSquare className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { navigate('/messages'); enablePush(); }} className="text-zinc-400 hover:text-[#06B6D4] relative" title="Direct Messages" data-testid="dashboard-messages-btn">
              <MessageSquare className="w-5 h-5" />
              {dmUnread > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#06B6D4] text-black text-[9px] font-bold flex items-center justify-center border border-[#050505]"
                  data-testid="dashboard-messages-badge"
                >
                  {dmUnread > 99 ? '99+' : dmUnread}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/library')} className="text-zinc-400 hover:text-[#06B6D4]" title="My Library (uploads + imported assets)" data-testid="dashboard-library-btn">
              <FolderOpen className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/marketplace')} className="text-zinc-400 hover:text-[#06B6D4]" title="Marketplace" data-testid="dashboard-marketplace-btn">
              <Store className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/premium')} className="text-amber-400 hover:text-amber-300" title="Premium" data-testid="dashboard-premium-btn">
              <Crown className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/trash')} className="text-zinc-400 hover:text-white" title="My Trash" data-testid="dashboard-trash-btn">
              <Trash2 className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="text-zinc-400 hover:text-white">
              <Settings className="w-5 h-5" />
            </Button>
            {user?.role === 'admin' && (
              <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} className="text-amber-400 hover:text-amber-300" title="Admin Panel">
                <Shield className="w-5 h-5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-zinc-400 hover:text-white">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Header with actions */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-white mb-1">My Games</h1>
            <p className="text-zinc-400 text-sm">{projects.length} project{projects.length !== 1 ? 's' : ''}</p>
          </div>

          <Button onClick={() => navigate('/generate')} variant="outline" className="border-[#06B6D4]/40 text-[#06B6D4] hover:bg-[#06B6D4]/10 mr-2" data-testid="dashboard-ai-studio-btn">
            <Sparkles className="w-4 h-4 mr-2" /> AI Game Studio
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="new-project-btn">
                <Plus className="w-4 h-4 mr-2" /> New Game
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0B] border-[#1E1E24] text-white">
              <DialogHeader>
                <DialogTitle className="text-xl font-bold">Create New Game</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-2 block">Game Name</label>
                  <Input 
                    placeholder="My Awesome Game" 
                    value={newName} 
                    onChange={(e) => setNewName(e.target.value)}
                    className="bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white"
                    data-testid="project-name-input"
                  />
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-2 block">Genre</label>
                  <Select value={newGenre} onValueChange={setNewGenre}>
                    <SelectTrigger className="bg-transparent border-[#1E1E24] text-white" data-testid="genre-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#121214] border-[#1E1E24]">
                      <SelectItem value="platformer">Platformer</SelectItem>
                      <SelectItem value="fps">First Person Shooter</SelectItem>
                      <SelectItem value="rpg">RPG Adventure</SelectItem>
                      <SelectItem value="puzzle">Puzzle</SelectItem>
                      <SelectItem value="simulation">Simulation</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-2 block">
                    AI Prompt <span className="text-zinc-600">(optional)</span>
                  </label>
                  <Input 
                    placeholder="A zombie survival game with crafting..." 
                    value={newPrompt} 
                    onChange={(e) => setNewPrompt(e.target.value)}
                    className="bg-transparent border-[#1E1E24] focus:border-[#06B6D4] text-white"
                    data-testid="ai-prompt-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Describe your game and AI will generate it</p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button 
                    onClick={() => createProject(false)} 
                    disabled={creating}
                    variant="outline"
                    className="flex-1 bg-transparent border-[#1E1E24] hover:bg-white/5"
                    data-testid="create-blank-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FolderOpen className="w-4 h-4 mr-2" />}
                    Blank Project
                  </Button>
                  <Button 
                    onClick={() => createProject(true)} 
                    disabled={creating || !newPrompt.trim()}
                    className="flex-1 bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
                    data-testid="generate-with-ai-btn"
                  >
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    Generate with AI
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/templates')}
                  className="text-xs text-zinc-500 hover:text-[#06B6D4] transition-colors text-center w-full pt-2"
                  data-testid="dashboard-templates-link"
                >
                  … or start from a ready-made template →
                </button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#06B6D4]" />
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-24 border border-dashed border-[#1E1E24] rounded-sm">
            <Gamepad2 className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">No games yet</h3>
            <p className="text-zinc-500 mb-6">Create your first game to get started</p>
            <Button onClick={() => setDialogOpen(true)} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90">
              <Plus className="w-4 h-4 mr-2" /> Create Game
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project, idx) => {
              const GenreIcon = genreIcons[project.genre] || Box;
              return (
                <div 
                  key={project.project_id}
                  className="project-card overflow-hidden animate-fade-in"
                  style={{ animationDelay: `${idx * 0.05}s` }}
                >
                  {/* Thumbnail */}
                  <div className="h-40 bg-[#121214] canvas-grid flex items-center justify-center overflow-hidden">
                    {project.thumbnail ? (
                      <img src={project.thumbnail} alt={project.name} className="w-full h-full object-cover" data-testid={`project-thumb-${project.project_id}`} />
                    ) : (
                      <GenreIcon className="w-12 h-12 text-zinc-700" />
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-bold text-white truncate">{project.name}</h3>
                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase border rounded-sm mt-1 ${genreColors[project.genre]}`}>
                          {project.genre}
                        </span>
                      </div>
                    </div>
                    
                    {project.description && (
                      <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{project.description}</p>
                    )}
                    
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90"
                        onClick={() => navigate(`/editor/${project.project_id}`)}
                        data-testid={`edit-project-${project.project_id}`}
                      >
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="bg-transparent border-[#1E1E24] hover:bg-white/5"
                        onClick={() => navigate(`/play/${project.project_id}`)}
                      >
                        <Play className="w-3 h-3" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        className="text-zinc-500 hover:text-red-500"
                        onClick={() => deleteProject(project.project_id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
